import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  CalendarCellUpdate,
  CalendarStateService,
  Event,
  Property,
} from '@app/modules/calendar/services/calendar-state.service';
import { addDays, format, isBefore, isSameDay, subDays } from 'date-fns';
import { filter, map } from 'rxjs/operators';

const dateManuallyBlocked = (property: Property, date: Date) => {
  const dateString = format(date, 'YYYY-MM-DD');
  return (
    property[dateString]?.available === false &&
    property[dateString]?.sources.find((source) => source.type === 'availability' && source.source === 'manual')
  );
};

@Component({
  standalone: false,
  selector: 'sbnb-calendar-cell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendar-cell.component.html',
  styleUrls: ['./calendar-cell.component.scss'],
})
export class CalendarCellComponent implements OnInit {
  @Input() calendarStartDate: Date;
  @Input() date: Date | null = null;
  @Input() propertyId: string;
  @Input() isClicked: boolean;
  @Input() isRangeSelected: boolean;
  @Input() optimisticUpdate: CalendarCellUpdate | undefined;
  @Output() reservationSelected: EventEmitter<{ event: Event; property: any }> = new EventEmitter();
  @Output() dateClicked: EventEmitter<{ date: string; property: any }> = new EventEmitter();
  @Output() dateHovered: EventEmitter<{ date: string; property: any }> = new EventEmitter();
  public Math = Math;
  public cellWidth = 50;
  public mode$ = this.calendarStateService.mode$;
  public calendarPermissions$ = this.calendarStateService.calendarPermissions$;
  public property$ = this.calendarStateService.properties$.pipe(
    filter((properties) => properties.length > 0),
    map((properties) => properties.find((property) => property.id == Number(this.propertyId)))
  );
  public todaysEvents$ = this.property$.pipe(
    filter((property) => Boolean(property)),
    map((property) => {
      if (!property?.events) return [];
      return property.events.filter((event) => {
        // this is in case we have an event that starts before the start of the calendar.
        // since it has no start cell to show it, we create it on the calendar start date and offset it to the left
        const calendarStart = new Date(this.calendarStartDate);
        const eventsBeforeStart =
          isSameDay(this.date, calendarStart) && isBefore(new Date(event.start_date), calendarStart);
        const eventsStartingToday = isSameDay(new Date(event.start_date), this.date);
        return eventsStartingToday || eventsBeforeStart;
      });
    })
  );

  public todaysTasks$ = this.property$.pipe(
    filter((property) => Boolean(property)),
    map((property) => {
      return property.tasks.filter((task) => {
        return isSameDay(new Date(task.start_date), this.date);
      });
    })
  );

  public isManualBlock$ = this.property$.pipe(
    filter((property) => Boolean(property)),
    map((property) => dateManuallyBlocked(property, this.date))
  );

  public prevSiblingManualBlock$ = this.property$.pipe(
    filter((property) => Boolean(property)),
    map((property) => dateManuallyBlocked(property, subDays(this.date, 1)))
  );

  public nextSiblingManualBlock$ = this.property$.pipe(
    filter((property) => Boolean(property)),
    map((property) => dateManuallyBlocked(property, addDays(this.date, 1)))
  );

  constructor(private readonly calendarStateService: CalendarStateService) {}

  ngOnInit(): void {}

  public trackById = (index: number, obj): number => {
    return obj.id;
  };
}
